import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import baseURL from '../Config';

const auth = () => {
    const login = JSON.parse(localStorage.getItem('adminUser') || localStorage.getItem('userDetails') || '{}');
    return { headers: { authorization: `Bearer ${login?.data?.token || login?.data?.accessToken}` }, timeout: 30000 };
};
const requestId = () => Array.from(window.crypto.getRandomValues(new Uint8Array(20)), b => b.toString(16).padStart(2, '0')).join('');

// Key the inner form by ticket so navigating cannot carry a draft or response to another ticket.
export default function SupportNotes(props) { return <Notes key={props.ticketId} {...props} />; }
function Notes({ ticketId, language }) {
    const t = (en, es) => language === 'es' ? es : en;
    const [notes, setNotes] = useState([]), [total, setTotal] = useState(0);
    const [page, setPage] = useState(1), [revision, setRevision] = useState(0);
    const [text, setText] = useState(''), [busy, setBusy] = useState(false), [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false), [saveError, setSaveError] = useState(false), [saved, setSaved] = useState(false);
    const attempt = useRef(null), saving = useRef(false);
    const url = `${baseURL}complaint/${encodeURIComponent(ticketId)}/support-notes`;
    useEffect(() => {
        let active = true;
        setLoading(true); setLoadError(false);
        axios.get(url, { ...auth(), params: { page } }).then(({ data }) => {
            if (!active) return;
            setNotes(data.notes); setTotal(data.total);
        }).catch(() => { if (active) setLoadError(true); }).finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [url, page, revision]);
    const save = async () => {
        if (saving.current || !text.trim()) return;
        saving.current = true; setBusy(true); setSaveError(false); setSaved(false);
        try {
            // Keep the same ID after a timeout: retries cannot append the same note twice.
            if (!attempt.current || attempt.current.text !== text.trim()) attempt.current = { text: text.trim(), requestId: requestId() };
            const { data } = await axios.post(url, attempt.current, auth());
            if (!data.note?._id) throw new Error('Missing saved note');
            setText(''); attempt.current = null; setSaved(true); setPage(1); setRevision(n => n + 1);
        } catch (_) { setSaveError(true); }
        finally { saving.current = false; setBusy(false); }
    };
    return <section className="surface-100 border-round p-3 my-3" aria-label={t('Support notes', 'Notas de seguimiento')}>
        <h3>{t('Support notes', 'Notas de seguimiento')}</h3>
        <p className="text-color-secondary">{t('Internal ticket comments. Record contact attempts, customer updates, and next steps. These are not sent to the customer.', 'Comentarios internos del ticket. Registre intentos de contacto, información del cliente y próximos pasos. No se envían al cliente.')}</p>
        <label htmlFor={`support-note-${ticketId}`}>{t('Add a comment', 'Agregar comentario')}</label>
        <InputTextarea id={`support-note-${ticketId}`} className="w-full my-2" rows={4} maxLength={4000} value={text} disabled={busy} onChange={e => { setText(e.target.value); setSaved(false); }} />
        <Button label={busy ? t('Saving…', 'Guardando…') : t('Save comment', 'Guardar comentario')} disabled={busy || !text.trim()} onClick={save} />
        {saveError && <p role="alert">{t('Could not confirm the save. Your text is retained; retry saving safely.', 'No se pudo confirmar el guardado. Se conserva el texto; puede reintentar guardarlo.')}</p>}
        {saved && <p role="status">{t('Comment saved.', 'Comentario guardado.')}</p>}
        <div className="flex align-items-center justify-content-between mt-3">
            <strong>{t('Comments', 'Comentarios')} ({total})</strong>
            <Button className="p-button-text" label={t('Refresh comments', 'Actualizar comentarios')} disabled={loading} onClick={() => setRevision(n => n + 1)} />
        </div>
        {loading ? <p role="status">{t('Loading comments…', 'Cargando comentarios…')}</p> : loadError ? <p role="alert">{t('Comments could not be loaded. Refresh to retry.', 'No se pudieron cargar los comentarios. Actualice para reintentar.')}</p> : <>
            {!notes.length && <p>{t('No comments yet.', 'Todavía no hay comentarios.')}</p>}
            {notes.map(note => {
                const author = [note.createdBy?.firstName, note.createdBy?.lastName].filter(Boolean).join(' ') || note.createdBy?.name || t('Author unavailable', 'Autor no disponible');
                return <article key={note._id} className="surface-card border-round p-3 my-2">
                    <strong>{author}</strong>
                    <div className="text-color-secondary text-sm">{new Intl.DateTimeFormat(language === 'es' ? 'es-PA' : 'en-US', { timeZone: 'America/Panama', dateStyle: 'medium', timeStyle: 'short' }).format(new Date(note.createdAt))} · {t('Panama time', 'Hora de Panamá')}</div>
                    <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{note.text}</p>
                </article>;
            })}
            {total > 20 && <div className="flex gap-2 align-items-center">
                <Button label={t('Previous', 'Anterior')} disabled={page === 1} onClick={() => setPage(n => n - 1)} />
                <span>{page} / {Math.ceil(total / 20)}</span>
                <Button label={t('Next', 'Siguiente')} disabled={page * 20 >= total} onClick={() => setPage(n => n + 1)} />
            </div>}
        </>}
    </section>;
}
